/**
 * Dates and durations, in Indonesian.
 *
 * Timezone is pinned to Asia/Jakarta rather than left to the browser. The
 * server stores TIMESTAMP WITH TIME ZONE and the business is in one timezone,
 * so a laptop still set to UTC from a trip must not show an approval as having
 * happened at seven in the morning. Pinning it also means two people reading
 * the same trail see the same times.
 */

const ZONE = 'Asia/Jakarta'
const LOCALE = 'id-ID'

const DATE = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
})

const DATE_TIME = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONE,
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

const TIME = new Intl.DateTimeFormat(LOCALE, {
  timeZone: ZONE,
  hour: '2-digit',
  minute: '2-digit',
})

function parse(value: string | null | undefined): Date | null {
  if (!value) return null
  const date = new Date(value)
  // An unparseable timestamp must render as a dash, never as "Invalid Date"
  // sitting in the middle of an approval trail.
  return Number.isNaN(date.getTime()) ? null : date
}

/** "13 Sep 2026", or "—" when there is nothing to show. */
export function formatDate(value: string | null | undefined): string {
  const date = parse(value)
  return date ? DATE.format(date) : '—'
}

/** "13 Sep 2026, 14.22" */
export function formatDateTime(value: string | null | undefined): string {
  const date = parse(value)
  return date ? DATE_TIME.format(date) : '—'
}

export function formatTime(value: string | null | undefined): string {
  const date = parse(value)
  return date ? TIME.format(date) : '—'
}

/**
 * How long something has been waiting, in words.
 *
 * Coarse on purpose. "2 hari" is what a person needs to know about a document
 * stuck in approval; "2 hari 4 jam 13 menit" is the same fact, harder to read,
 * and it changes while you look at it.
 */
export function relativeTime(value: string | null | undefined, now: Date = new Date()): string {
  const date = parse(value)
  if (!date) return '—'

  const seconds = Math.round((now.getTime() - date.getTime()) / 1000)
  const future = seconds < 0
  const abs = Math.abs(seconds)

  const phrase =
    abs < 60
      ? 'kurang dari semenit'
      : abs < 3600
        ? `${Math.floor(abs / 60)} menit`
        : abs < 86_400
          ? `${Math.floor(abs / 3600)} jam`
          : abs < 2_592_000
            ? `${Math.floor(abs / 86_400)} hari`
            : abs < 31_536_000
              ? `${Math.floor(abs / 2_592_000)} bulan`
              : `${Math.floor(abs / 31_536_000)} tahun`

  if (abs < 60) return future ? 'sebentar lagi' : 'baru saja'
  return future ? `dalam ${phrase}` : `${phrase} lalu`
}

/**
 * How long a step took, from activation to completion.
 *
 * Separate from relativeTime because a duration is not an age: "3 jam" as a
 * time-in-step reads differently from "3 jam lalu".
 */
export function formatDuration(
  from: string | null | undefined,
  to: string | null | undefined,
): string {
  const start = parse(from)
  const end = parse(to)
  if (!start || !end) return '—'

  const minutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60_000))
  if (minutes < 60) return `${minutes} menit`
  if (minutes < 1440) {
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    return rest === 0 ? `${hours} jam` : `${hours} jam ${rest} menit`
  }
  const days = Math.floor(minutes / 1440)
  const hours = Math.floor((minutes % 1440) / 60)
  return hours === 0 ? `${days} hari` : `${days} hari ${hours} jam`
}
